/**
 * Feed configuration for g1's live data path — resolves the set of upstream
 * Hydra-protocol feeds the tab should consume.
 *
 * Background: g1 historically read ALL sports from one firehose hydra over a
 * single SharedWorker. V3 ("satellite hydras") splits sports onto per-sport
 * edge engines (hydraedge), each serving the same consumer WS protocol on its
 * own URL. g1 must consume the firehose AND the edges simultaneously — the
 * firehose isn't going away. Rather than teach the SharedWorker to juggle N
 * upstream sockets (its connection loop is load-bearing and full of hard-won
 * stall/backpressure fixes), we run one SharedWorker instance PER feed (keyed
 * by `workerName`) and merge their frames into the single, source-agnostic
 * `marketStore`. See `useHydra` for the wiring.
 *
 * Ownership / cutover: each edge feed declares the sports it is authoritative
 * for. The firehose is the catch-all for every sport no edge has claimed. A
 * market frame is ingested only by the feed that owns its sport, so a sport
 * served by both (e.g. MLB on a multi-sport edge node) doesn't flap — and
 * pointing a single sport at an edge while the rest stay on the firehose is
 * exactly the per-sport shadow cutover the rebuild calls for.
 *
 * Config (env, build-time):
 *   VITE_HYDRA_URL          firehose consumer WS (defaults to prod hydra)
 *   VITE_HYDRA_EDGE_FEEDS   ';'-separated `url=sport,sport` entries, e.g.
 *                           `ws://localhost:8891/ws=nfl`
 */

import type { Sport } from '@/views/ValueDashboard/types';
import { normalizeSport } from '@/stores/marketStore';

export interface FeedConfig {
  /** Stable id used as the activeSports source key. */
  id: string;
  /** Consumer WS URL (browser-resolvable — the browser dials this, not the
   *  g1 container). */
  url: string;
  /** SharedWorker instance name — MUST be unique per feed, else two feeds
   *  share one worker (and one upstream URL). */
  workerName: string;
  role: 'firehose' | 'edge';
  /** True when this feed should ingest a frame for `sport`. Edge feeds accept
   *  only the sports they own; the firehose accepts every sport NOT claimed by
   *  an edge (and frames with no/unknown sport — only the firehose is the
   *  catch-all). */
  accepts: (sport: Sport | null) => boolean;
}

function defaultHydraWsUrl(): string {
  // Consumer WS is on the API port (8891), not :443 (the public dashboard).
  // Local Vite is http://localhost, so plain ws to :8891 is allowed in dev.
  return import.meta.env.DEV ? 'ws://hydra.newbx.com:8891/ws' : 'wss://hydra.newbx.com/ws';
}

interface ParsedEdge {
  url: string;
  ownsSports: Set<Sport>;
}

/** Parse `url=sport,sport;url2=sport` into edge descriptors. Malformed entries
 *  (no `=`, empty url/sports, no recognized sport) are skipped, not thrown —
 *  a typo in env shouldn't kill the whole live feed. */
function parseEdgeFeeds(raw: string): ParsedEdge[] {
  const out: ParsedEdge[] = [];
  for (const entry of raw.split(';').map((s) => s.trim()).filter(Boolean)) {
    const eq = entry.indexOf('=');
    if (eq < 0) continue;
    const url = entry.slice(0, eq).trim();
    const sportsPart = entry.slice(eq + 1).trim();
    if (!url || !sportsPart) continue;
    const ownsSports = new Set<Sport>();
    for (const code of sportsPart.split(',').map((s) => s.trim()).filter(Boolean)) {
      const s = normalizeSport(code);
      if (s) ownsSports.add(s);
    }
    if (ownsSports.size === 0) continue;
    out.push({ url, ownsSports });
  }
  return out;
}

/** Resolve the live feeds from env. Always returns the firehose first,
 *  followed by any configured edges. */
export function resolveFeeds(): FeedConfig[] {
  const firehoseUrl =
    (import.meta.env.VITE_HYDRA_URL as string | undefined)?.trim() || defaultHydraWsUrl();
  const edges = parseEdgeFeeds((import.meta.env.VITE_HYDRA_EDGE_FEEDS as string | undefined) ?? '');

  // Union of edge-claimed sports — the firehose excludes these so a claimed
  // sport is served by exactly one feed.
  const claimed = new Set<Sport>();
  for (const e of edges) for (const s of e.ownsSports) claimed.add(s);

  const firehose: FeedConfig = {
    id: 'firehose',
    url: firehoseUrl,
    workerName: 'hydra-shared', // unchanged name → existing prod path untouched
    role: 'firehose',
    accepts: (sport) => sport == null || !claimed.has(sport),
  };

  const edgeFeeds: FeedConfig[] = edges.map((e, i) => ({
    id: `edge:${i}`,
    url: e.url,
    workerName: `hydra-edge-${i}`,
    role: 'edge' as const,
    accepts: (sport) => sport != null && e.ownsSports.has(sport),
  }));

  return [firehose, ...edgeFeeds];
}
